import { z } from 'zod';
import { captureException } from '@sentry/vue';
import { getCookieValue } from '../utils/cookies.ts';
import { getDecoratedUrlWithConversionLinker } from "../utils/getDecoratedUrlWithConversionLinker.ts";

/**
 * This file handles the integration with the Didomi Consent Management Platform (CMP),
 * enabling the retrieval, parsing, and formatting of user consent data to append
 * it as a query parameter to URLs. It also includes error handling for scenarios
 * where the Didomi library is unavailable or returns invalid data.
 *
 * Key functionalities:
 * - Validate and structure consent data using Zod schemas.
 * - Extract consent data from the URL hash fragment.
 * - Retrieve current consent status from the Didomi library when available.
 * - Format consent data into a JSON structure and append it to the redirect URL.
 * - Log exceptions via Sentry when errors occur.
 *
 * For more details, refer to:
 * - Didomi API documentation: https://developers.didomi.io/cmp/web-sdk/pass-choices-in-query-string
 * - getCurrentUserStatus API: https://developers.didomi.io/cmp/web-sdk/reference/api#getcurrentuserstatus
 */

type DecodedBearer = {
    created?: string;
    updated?: string;
    user_id?: string;
    vendors?: { disabled?: (string|number)[]; enabled?: (string|number)[] };
    vendors_li?: { disabled?: (string|number)[]; enabled?: (string|number)[] };
    version?: number;
};

const enabledDisabledSchema = z.object({
    enabled: z.array(z.string().or(z.number())),
    disabled: z.array(z.string().or(z.number())),
});

const userStatusSchema = z.object({
    addtl_consent: z.string(),
    consent_string: z.string(),
    created: z.string(),
    purposes: z.object({
        consent: enabledDisabledSchema,
        essential: z.array(z.string()),
        global: enabledDisabledSchema,
        legitimate_interest: enabledDisabledSchema,
    }),
    updated: z.string(),
    user_id: z.string(),
    vendors: z.object({
        consent: enabledDisabledSchema,
        global: enabledDisabledSchema,
        global_consent: enabledDisabledSchema,
        global_li: enabledDisabledSchema,
        legitimate_interest: enabledDisabledSchema,
    }),
});

const purposesSchema = z.record(z.string(),
    z.object({
        description: z.string().optional(),
        id: z.string(),
        illustrations: z.array(z.string()).optional(),
        name: z.string().optional(),
        namespace: z.string().optional().or(z.undefined()),
    })
);

const vendorsSchema = z.array(
    z.object({
        didomiId: z.string().or(z.undefined()),
        featureIds: z.array(z.number()).or(z.undefined()),
        flexiblePurposeIds: z.array(z.string()).or(z.undefined()),
        id: z.number().or(z.string()),
        legIntPurposeIds: z.array(z.string()),
        namespace: z.string().or(z.undefined()),
        purposeIds: z.array(z.string()),
        specialFeatureIds: z.array(z.number()).or(z.undefined()),
        specialPurposeIds: z.array(z.number()).or(z.undefined()),
        tmpDeletedDate: z.string().or(z.undefined()),
    })
);


const itemSchema = z.object({
    id: z.string(),
    enabled: z.boolean(),
});

const consentDataSchema = z.object({
    vendors: z.record(z.string(), itemSchema),
    purposes: z.record(z.string(), itemSchema),
});

declare const window: Window & {
    Didomi?: {
        getCurrentUserStatus: () => z.infer<typeof consentDataSchema>;
        getUserStatus: () => z.infer<typeof userStatusSchema>;
        getPurposes: () => z.infer<typeof purposesSchema>;
        getVendors: () => z.infer<typeof vendorsSchema>;
    };
};

const DIDOMI_PARAM_KEY = 'didomiConfig.user.externalConsent.value';
const DIDOMI_PARAM_APPLY_GLOBALLY_KEY = 'didomiConfig.user.externalConsent.applyGlobally';

/**
 * Builds a URL with the given consent data appended as a query parameter.
 */
function buildUrl({ currentUrlToRedirect, externalConsentValue }: { currentUrlToRedirect: string; externalConsentValue: string }): string {
    const url = new URL(currentUrlToRedirect);
    url.searchParams.set(DIDOMI_PARAM_KEY, externalConsentValue);
    url.searchParams.set(DIDOMI_PARAM_APPLY_GLOBALLY_KEY, 'true');
    return url.toString();
}

/**
 * Extracts a query parameter value from the URL hash fragment.
 */
function getHashParamValue(): string | null {
    const hash = window.location.hash || window.location.search;
    const queryString = hash.includes('?') ? hash.split('?')[1] : '';
    return new URLSearchParams(queryString).get(DIDOMI_PARAM_KEY);
}

/**
 * Retrieves and formats the Didomi consent data, then builds the redirect URL.
 *
 * @param {string} currentUrlToRedirect - The URL to redirect to.
 * @returns {string} The URL with the consent data appended as a query parameter, or the original URL if consent data is unavailable.
 */
export function buildUrlWithConsent({ currentUrlToRedirect }: { currentUrlToRedirect: string }): string {
    // Convert relative URLs to absolute URLs.
    if (currentUrlToRedirect.startsWith('/')) {
        currentUrlToRedirect = new URL(currentUrlToRedirect, window.location.origin).href;
    }

    // Check if the consent data is provided in the URL hash fragment.
    const externalConsentValue = getHashParamValue();
    if (externalConsentValue) {
        const urlDidomi =  buildUrl({ currentUrlToRedirect, externalConsentValue });
        return getDecoratedUrlWithConversionLinker(urlDidomi) || urlDidomi;
    }

    // Check if the Didomi library is loaded.
    if (typeof window.Didomi?.getCurrentUserStatus !== 'function' && typeof window.Didomi?.getUserStatus !== 'function' && typeof window.Didomi?.getPurposes !== 'function' && typeof window.Didomi?.getVendors !== 'function') {
        return currentUrlToRedirect;
    }

    try {
        // https://developers.didomi.io/cmp/web-sdk/reference/api#getcurrentuserstatus
        const status = userStatusSchema.parse(window.Didomi.getUserStatus());
        const allPurposes = Object.values(purposesSchema.parse(window.Didomi.getPurposes()));
        const allVendors = vendorsSchema.parse(window.Didomi.getVendors());

        // 1) Get only the enabled
        const pc = status.purposes.consent.enabled;
        const pli = status.purposes.legitimate_interest.enabled;
        const vc = status.vendors.consent.enabled;
        const vcd = Object.entries(consentDataSchema.parse(window.Didomi.getCurrentUserStatus()).vendors)
            .filter(([, vendor]) => !vendor.enabled)
            .map(([key]) => key === 'google' ? 'google' : `c:${key}`);
        const vli = status.vendors.legitimate_interest.enabled;
        let vlid = [] as (string|number)[];

        try {
            vlid = (JSON.parse(atob(getCookieValue('didomi_token') ?? '')) as DecodedBearer).vendors_li?.disabled ?? [];
        } catch {
            /* empty */
        }

        // 2) IAB : number for TCF (optional if you are not in TCF)
        const iabPurposes = allPurposes
            .filter((p) => p.namespace === 'iab' && pc.includes(p.id))
            .map((p) => parseInt(p.id, 10))
            .filter(Number.isInteger);

        const iabVendors = allVendors
            .filter((v) => v.namespace === 'iab' && ((v.didomiId && vc.includes(v.didomiId)) || vc.includes(v.id)))
            .map((v) => v.id)
            .filter(Number.isInteger);

        const externalConsent = {
            purposes: {
                consent: { enabled: pc, disabled: [] },
                legitimate_interest: { enabled: pli, disabled: [] },
            },
            vendors: {
                consent: { enabled: vc, disabled: vcd },
                legitimate_interest: { enabled: vli, disabled: vlid },
            },
            iab: {
                purposes: {
                    consent: iabPurposes,
                    legitimate_interest: [],
                },
                vendors: {
                    consent: iabVendors,
                    legitimate_interest: [],
                },
            },
        };

         const urlWithDidomi = buildUrl({ currentUrlToRedirect, externalConsentValue: JSON.stringify(externalConsent) });
         return getDecoratedUrlWithConversionLinker(urlWithDidomi) || urlWithDidomi;
    } catch (error) {
        captureException(error);
        return currentUrlToRedirect;
    }
}
